import * as React from "react";
import * as ReactDOM from "react-dom";
import * as globals from "../../globals";
import * as moment from "moment";
import * as modelsGet from "../../models/get";
import * as modelsChangeset from "../../models/changeset";
import * as modelsForm from "../../models/form";
import * as calendarUtils from "../../utils/calendar";
import * as apiStatus from "../../api/status";
import * as apiAppointments from "../../api/appointments";
import { Loader, LoaderStyle } from "../loader";
import { ComboBox, ComboBoxType, DropDownTextItem, DROPDOWN_TEXT_ITEM_HEIGHT } from "../comboBox";
import { ScrollViewer, ScrollBarVisibility } from "../scrollViewer";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";

interface IProperties
{
    /**
     * Angabe, ob der Kalender aktualisiert werden soll.
     */
    refresh: boolean,

    /**
     * Wird ausgeführt sobald sich das Popup für die Termindetails öffnet.
     * @param e Das auslösende Ereignis.
     * @param event Der gebundene Termin.
     */
    onEventDetailsPopupOpening?: (e: MouseEvent, appointment: modelsGet.IAppointment) => boolean
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Der momentan angemeldete Benutzer.
     */
    user?: modelsGet.IUser,

    /**
     * Alle in der Datenbank eingerichteten Status Einträge incl. des pseudo Status "Alle".
     */
    status?: modelsGet.IStatus[],

    /**
     * Enthält alle in der Zukunft anstehenden Termine.
     */
    appointments?: modelsGet.IAppointment[],

    /**
     * Enthält alle von der Komponente benötigten Einstellungen.
     */
    settings?: {
        eventDayViewTemplate?: string
    }

    /**
     * Enthält Werte von input Elementen.
     */
    form?: {
        status?: modelsForm.IComboBoxValue<string, number>
    }
}

export class SideBarEvents extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Komponente @see SideBarEvents.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true,
            settings: {},
            form: {
                status: {}
            }
        };

        this.initAsync(props);
    }

    /**
     * Wird ausgeführt, sobald die Komponente neue Eigenschaften erhält.
     * Hier werden die benötigten Daten erneut geladen.
     * @param nextProps Die nächsten/neuen Eigenschaften.
     */
    componentWillReceiveProps(nextProps: IProperties)
    {
        const newProp = nextProps;
        if (newProp.refresh)
        {
            apiAppointments.getAppointments(moment(), moment().add(1, "months"), true).then((data) =>
            {
                var appointments: modelsGet.IAppointment[] = data;
                appointments.removeAt(a => a.appointment_type !== "appointment");

                this.setState({
                    appointments: appointments
                });
            });
        }
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        // Momentan angemeldeten Benutzer laden.
        var currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();

        // Alle Status Einträge laden.
        var status: modelsGet.IStatus[] = await apiStatus.getStatus();
        status.unshift({
            id: -1,
            icon: "test",
            status: "Alle",
            status_uri_encoded: "alle",
            order_nr: 0,
            color: "#fff",
            is_default: false
        });

        // Alle anstehenden Termine laden.
        var appointments: modelsGet.IAppointment[] = await apiAppointments.getAppointments(moment(), moment().add(1, "months"), true);
        appointments.removeAt(a => a.appointment_type !== "appointment");

        // Benötigte Einstellungen laden.
        var eventDayViewTemplate = await globals.getSettingAsync<string>(globals.SettingKey.eventDVTemplate)

        // State setzen.
        this.setState({
            isLoading: false,
            user: currentUser,
            status: status,
            appointments: appointments,
            settings: {
                eventDayViewTemplate: eventDayViewTemplate
            },
            form: {
                status: {
                    value: "Alle",
                    selectedValue: -1
                }
            }
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert in der Filter ComboBox ändert.
     * @param selectedItem Der neu ausgewählte Status.
     */
    private onSelectionChanged = (selectedItem: modelsGet.IStatus) =>
    {
        this.setState({
            form: {
                status: {
                    value: selectedItem && selectedItem.status,
                    selectedValue: selectedItem && selectedItem.id
                }
            }
        });
    }

    /**
     * Gibt die Überschrift für die Rubriken in der Liste für die anstehenden Termine zurück.
     * @param appointment Termin zum ermitteln des Textes.
     */
    public getHeaderText(appointment: modelsGet.IAppointment): string
    {
        var today: moment.Moment = moment();
        var lastDate: moment.Moment;
        var { appointments } = this.state;
        var index: number = appointments.indexOf(appointment);

        if (index > 0)
        {
            lastDate = moment(appointments.find((value, idx, obj) => idx === index - 1).starttime)
        }

        let date: moment.Moment = moment(appointment.starttime);
        if (lastDate == null || !date.isSame(lastDate, "day"))
        {
            if (date.isSame(today, "day"))
            {
                return "Heute " + date.format("L");
            }
            else if (date.isSame(moment(today).add(1, "days"), "day"))
            {
                return "Morgen " + date.format("L");
            }
            else
            {
                return date.format("dddd") + " " + date.format("L");
            }
        }
    }

    /**
     * Ermittelt den richtigen Betreff der angezeigt werden soll. Je nach eingerichtetem Template oder
     * Benutzerrecht, kann die Anzeige variieren.
     * @param appointment Termin zum ermitteln des Betreffs.
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin einsehen darf.
     */
    private getSubject(appointment: modelsGet.IAppointment, isUserAuthorizedToSeeAppointment: boolean): string
    {
        if (isUserAuthorizedToSeeAppointment)
        {
            const { settings } = this.state;
            var text: string = "";

            if (settings.eventDayViewTemplate === "EventType2")
            {
                if (appointment.persons != null)
                {
                    var deceased: modelsGet.IPerson = appointment.persons.where(p => p.is_deceased).first();
                    if (deceased != null)
                        return deceased.fullname_rev;
                    else
                        return appointment.subject;
                }
                else
                {
                    return appointment.subject;
                }
            }
            else
            {
                return appointment.calendar_name + ", " + appointment.subject;
            }
        }
        else
        {
            return appointment.calendar_name + ", Belegt";;
        }
    }

    /**
     * Rendert die anstehenden Termine.
     */
    private renderAppointments(): JSX.Element[]
    {
        var elements: JSX.Element[] = [];
        var { onEventDetailsPopupOpening } = this.props;
        var { user, appointments, form } = this.state;

        for (let appointment of appointments.where(a => (form.status.selectedValue && form.status.selectedValue < 0 ? true : a.status_id === form.status.selectedValue)))
        {
            let isUserAuthorizedToSeeAppointment: boolean = appointment.appointment_type !== "not_authorized";
            let headerText: string = this.getHeaderText(appointment);
            
            if (headerText != null)
                elements.push(<li key={headerText} className="calendar-sidebar_events_header">{headerText}</li>);

            elements.push(
                <PopupProvider
                    id="event-contextmenu"
                    trigger={PopupTrigger.RightClick}
                    triggerStopPropagation={true}
                    dataSource={{
                        isSideBar: true,
                        calendar: appointment.calendar_id,
                        appointment: appointment
                    }}>
                    <PopupProvider
                        id="event-info"
                        trigger={PopupTrigger.Hover}
                        triggerDelay={1000}
                        dataSource={appointment}
                        onOpening={onEventDetailsPopupOpening}>
                        <li
                            key={appointment.id}
                            className="calendar-sidebar_events_event">
                            <span className="calendar-sidebar_events_event_time">{moment(appointment.starttime).format('LT')}</span>
                            <div
                                className="calendar-sidebar_events_event_color"
                                style={{
                                    backgroundColor: (appointment.status_id > 0) ? appointment.status_color : appointment.calendar_color
                                }}></div>
                            <span className="calendar-sidebar_events_event_subject">{this.getSubject(appointment, isUserAuthorizedToSeeAppointment)}</span>
                        </li>
                    </PopupProvider>
                </PopupProvider>
            );
        }

        return elements.length > 0 ? elements : [<span className="calendar-sidebar_events_empty">Es liegen in der nächsten Zeit keine Termine an.</span>];
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, user, status, form } = this.state;
        
        if (isLoading)
        {
            return (
                <div className="calendar-sidebar_events">
                    <Loader loaderStyle={LoaderStyle.style1} />
                </div>
            );
        }

        if (user != null && user.settings.style.hideUpcomingEvents)
        {
            return null;
        }

        return (
            <div className="calendar-sidebar_events">
                <ComboBox
                    dataSource={status}
                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                    comboBoxType={ComboBoxType.dropDownList}
                    value={form.status.value}
                    selectedValue={form.status.selectedValue}
                    onSelectionChanged={this.onSelectionChanged}
                    valueMember="id"
                    displayMember="status">
                    {
                        (status: modelsGet.IStatus) =>
                        {
                            return (
                                <DropDownTextItem
                                    key={status.id}
                                    value={status.id}
                                    displayValue={status.status} />
                            );
                        }
                    }
                </ComboBox>
                <ScrollViewer
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    style={{
                        top: 50,
                        paddingLeft: 10,
                        paddingRight: 5
                    }}
                    styleWhenNoScrollBar={{
                        top: 50,
                        paddingLeft: 10,
                        paddingRight: 10
                    }}>
                    <ul style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0
                    }}>
                        {this.renderAppointments()}
                    </ul>
                </ScrollViewer>
            </div>
        );
    }
}